import type { CoverageDto } from '../../../../../../../types';
import type { CoverageDetailsProgress } from '../../../types/endorsement-workflow.types';
import type { deriveFormattedPolicyDetails } from '../../../utils/endorsement-workflow.utils';

export const MTE_GLOBALS_MAPPING = Object.freeze({
  aggregateLimit: 'Aggregate Limit',
  deductible: 'Retention',
  waitingPeriod: 'Waiting Period',
  retroactivePeriod: 'Retro Active Period',
  hasTria: 'TRIA',
} as const);

export const RETROACTIVE_PERIODS = Object.freeze({
  1: '1Y',
  2: '2Y',
  3: '3Y',
  '-1': 'FPA',
} as const);

export const MTEGlobals = Object.keys(MTE_GLOBALS_MAPPING);
type MTEGlobal = keyof typeof MTE_GLOBALS_MAPPING;

export interface CoverageSummaryProps {
  policyDetails: ReturnType<typeof deriveFormattedPolicyDetails>;
  progressData: CoverageDetailsProgress['payload'];
}

interface MergedCoverages {
  [key: string]: {
    endorsement: CoverageDto | null;
    policy: CoverageDto | null;
  };
}

interface CoverageGroupDeltas {
  [key: string]: {
    endorsement: CoverageDto | null;
    policy: CoverageDto | null;
    isDelta: boolean;
  }[];
}

export const composeCoverageDeltasByGroupNames = ({
  policyDetails,
  progressData,
}: CoverageSummaryProps) => {
  // creates object with coverage names as keys from endorsement data
  const endorsementCoverages = progressData.coverages.reduce(
    (acc: MergedCoverages, coverage) => {
      if (!acc[coverage.name]) {
        acc[coverage.name] = { endorsement: coverage, policy: null };
      }
      return acc;
    },
    {}
  );

  // adds policy to above object
  const allCoverages = (policyDetails.coverages as CoverageDto[]).reduce(
    (acc: MergedCoverages, coverage) => {
      if (!acc[coverage.name]) {
        acc[coverage.name] = {
          endorsement: null,
          policy: coverage,
        };
      } else {
        acc[coverage.name] = {
          endorsement: acc[coverage.name].endorsement,
          policy: coverage,
        };
      }

      return acc;
    },
    endorsementCoverages
  );

  // creates object with the coverage groups as keys
  const coveragesWithGroups = Object.keys(allCoverages).reduce(
    (acc: CoverageGroupDeltas, coverageName) => {
      const groupName =
        allCoverages[coverageName].endorsement?.groupName ||
        allCoverages[coverageName].policy?.groupName;

      const isDelta = getIsCoverageDelta({
        endorsementCoverage: allCoverages[coverageName].endorsement,
        policyCoverage: allCoverages[coverageName].policy,
      });

      if (groupName && !acc[groupName]?.length) {
        acc[groupName] = [];
        acc[groupName].push({
          isDelta,
          ...allCoverages[coverageName],
        });
      } else if (groupName && acc[groupName]?.length) {
        acc[groupName].push({
          isDelta,
          ...allCoverages[coverageName],
        });
      }
      return acc;
    },
    {}
  );

  return coveragesWithGroups;
};

export const composeGlobalDeltas = ({
  policyDetails,
  progressData,
}: CoverageSummaryProps) => {
  const mergedGlobals = MTEGlobals.map((global) => {
    let isDelta;

    if (global === 'waitingPeriod') {
      isDelta =
        Boolean(progressData[global as keyof typeof progressData]) !==
        Boolean(policyDetails[global as keyof typeof policyDetails]);
    } else {
      isDelta =
        progressData[global as keyof typeof progressData] !==
        policyDetails[global as keyof typeof policyDetails];
    }

    return {
      label: MTE_GLOBALS_MAPPING[global as MTEGlobal],
      endorsement: progressData[global as keyof typeof progressData] || '-',
      policy: policyDetails[global as keyof typeof policyDetails] || '-',
      isDelta,
    };
  });

  return mergedGlobals;
};

const getIsCoverageDelta = ({
  endorsementCoverage,
  policyCoverage,
}: {
  endorsementCoverage: CoverageDto | null;
  policyCoverage: CoverageDto | null;
}) => {
  return (
    endorsementCoverage?.limit !== policyCoverage?.limit ||
    endorsementCoverage?.deductible !== policyCoverage?.deductible ||
    endorsementCoverage?.retroActivePeriod !==
      policyCoverage?.retroActivePeriod ||
    /* either endorsement coverage or policy coverage can be null, but its not possible for both to be null. If they are null, they are not selected.  */
    endorsementCoverage == null ||
    policyCoverage == null
  );
};
